import { Aircraft, Transaction, Event } from '../types/intellijetnet'
import { ContainerType, AircraftMovement, OffMarket } from '../types/database'

/**
 * Validation result for aircraft movement
 */
export interface MovementValidationResult {
  isValid: boolean
  reason?: string
  warnings?: string[]
  requiresConfirmation?: boolean
  confirmationMessage?: string
}

/**
 * Context for movement validation
 */
export interface MovementValidationContext {
  aircraft: Aircraft
  fromContainer: ContainerType
  toContainer: ContainerType
  
  // Current state data
  existingTransaction?: Transaction | null
  existingEvent?: Event | null
  offMarketRecord?: OffMarket | null
  
  // API presence checks
  isInApiForSale?: boolean
  isInApiSold?: boolean
  isInApiRemoved?: boolean
  
  // Recent movements
  recentMovements?: AircraftMovement[]
  lastSnapshotDate?: string | null
}

/**
 * Validation rules for aircraft movements
 */
export class AircraftMovementValidator {
  
  /**
   * Validate if an aircraft can be moved between containers
   */
  static validateMovement(context: MovementValidationContext): MovementValidationResult {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { aircraft: _aircraft, fromContainer, toContainer } = context

    // Basic validation
    if (fromContainer === toContainer) {
      return {
        isValid: false,
        reason: 'Cannot move aircraft to the same container'
      }
    }
    
    // Route-specific validation
    switch (`${fromContainer}_to_${toContainer}`) {
      case 'for_sale_to_sold':
        return this.validateForSaleToSold(context)
      
      case 'for_sale_to_removed_from_market':
        return this.validateForSaleToRemoved(context)
      
      case 'removed_from_market_to_sold':
        return this.validateRemovedToSold(context)
      
      case 'removed_from_market_to_for_sale':
        return this.validateRemovedToForSale(context)
      
      case 'sold_to_for_sale':
        return this.validateSoldToForSale(context)
      
      case 'sold_to_removed_from_market':
        return this.validateSoldToRemoved(context)
      
      default:
        return {
          isValid: false,
          reason: `Movement from ${fromContainer} to ${toContainer} is not supported`
        }
    }
  }
  
  /**
   * Validate moving from For Sale to Sold
   */
  private static validateForSaleToSold(_context: MovementValidationContext): MovementValidationResult {
    // Generally allowed - creates a transaction snapshot
    return {
      isValid: true,
      requiresConfirmation: true,
      confirmationMessage: 'This will create a transaction snapshot and move the aircraft to the Sold container. Continue?'
    }
  }
  
  /**
   * Validate moving from For Sale to Removed
   */
  private static validateForSaleToRemoved(_context: MovementValidationContext): MovementValidationResult {
    // Generally allowed
    return {
      isValid: true,
      requiresConfirmation: true,
      confirmationMessage: 'This will move the aircraft to the Removed from Market container. Continue?'
    }
  }
  
  /**
   * Validate moving from Removed to Sold
   */
  private static validateRemovedToSold(_context: MovementValidationContext): MovementValidationResult {
    // Generally allowed - creates a transaction snapshot
    return {
      isValid: true,
      requiresConfirmation: true,
      confirmationMessage: 'This will create a transaction snapshot and move the aircraft to the Sold container. Continue?'
    }
  }
  
  /**
   * Validate moving from Removed to For Sale
   */
  private static validateRemovedToForSale(_context: MovementValidationContext): MovementValidationResult {
    // Generally allowed
    return {
      isValid: true,
      requiresConfirmation: true,
      confirmationMessage: 'This will move the aircraft back to the For Sale container. Continue?'
    }
  }
  
  /**
   * Validate moving from Sold to For Sale (RESTRICTED)
   */
  private static validateSoldToForSale(context: MovementValidationContext): MovementValidationResult {
    return this.validateSoldToAny(context, 'for_sale')
  }
  
  /**
   * Validate moving from Sold to Removed (RESTRICTED)
   */
  private static validateSoldToRemoved(context: MovementValidationContext): MovementValidationResult {
    return this.validateSoldToAny(context, 'removed_from_market')
  }
  
  /**
   * Validate moving FROM Sold container (applies restrictions)
   */
  private static validateSoldToAny(context: MovementValidationContext, toContainer: ContainerType): MovementValidationResult {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { aircraft: _aircraft, isInApiForSale, isInApiRemoved, offMarketRecord, lastSnapshotDate } = context

    // Check if aircraft exists in target container via API
    const existsInTargetViaApi = (toContainer === 'for_sale' && isInApiForSale) || 
                                (toContainer === 'removed_from_market' && isInApiRemoved)
    
    if (existsInTargetViaApi) {
      return {
        isValid: true,
        requiresConfirmation: true,
        confirmationMessage: `Aircraft exists in ${toContainer.replace('_', ' ')} via API. This will remove the transaction snapshot and move the aircraft. Continue?`,
        warnings: ['This will delete the transaction snapshot as the aircraft is no longer sold.']
      }
    }
    
    // Check if aircraft is user-flagged as off-market
    const isOffMarket = offMarketRecord !== null && offMarketRecord !== undefined
    
    if (!isOffMarket) {
      return {
        isValid: false,
        reason: 'Cannot move from Sold container unless the aircraft exists in the target container via API or is flagged as off-market.'
      }
    }
    
    // Check time constraint for off-market aircraft
    if (lastSnapshotDate) {
      const snapshotDate = new Date(lastSnapshotDate)
      const now = new Date()
      const daysSinceSnapshot = (now.getTime() - snapshotDate.getTime()) / (1000 * 60 * 60 * 24)
      
      if (daysSinceSnapshot > 14) {
        return {
          isValid: false,
          reason: `Cannot move off-market aircraft from Sold container after 14 days. Snapshot was created ${Math.floor(daysSinceSnapshot)} days ago.`
        }
      }
      
      return {
        isValid: true,
        requiresConfirmation: true,
        confirmationMessage: `This off-market aircraft will be moved from Sold to ${toContainer.replace('_', ' ')}. The transaction snapshot will be deleted. Continue?`,
        warnings: [
          'This will delete the transaction snapshot.',
          `Snapshot was created ${Math.floor(daysSinceSnapshot)} days ago (within 14-day limit).`
        ]
      }
    }
    
    // No snapshot date available - allow but warn
    return {
      isValid: true,
      requiresConfirmation: true,
      confirmationMessage: `This off-market aircraft will be moved from Sold to ${toContainer.replace('_', ' ')}. Continue?`,
      warnings: ['Could not determine snapshot age - proceeding with caution.']
    }
  }
  
  /**
   * Check if aircraft has recent movements that might conflict
   */
  static checkRecentMovements(context: MovementValidationContext): string[] {
    const warnings: string[] = []
    const { recentMovements } = context
    
    if (!recentMovements || recentMovements.length === 0) {
      return warnings
    }
    
    // Check for recent movements in the last 24 hours
    const oneDayAgo = new Date(Date.now() - 24 * 60 * 60 * 1000)
    const recentMoves = recentMovements.filter(m => new Date(m.moved_at) > oneDayAgo)
    
    if (recentMoves.length > 0) {
      warnings.push(`This aircraft has ${recentMoves.length} recent movement(s) in the last 24 hours.`)
    }
    
    // Check for pending movements
    const pendingMoves = recentMovements.filter(m => m.status === 'pending')
    if (pendingMoves.length > 0) {
      warnings.push(`This aircraft has ${pendingMoves.length} pending movement(s) that can still be undone.`)
    }
    
    return warnings
  }
  
  /**
   * Get container display name
   */
  static getContainerDisplayName(container: ContainerType): string {
    switch (container) {
      case 'for_sale':
        return 'For Sale'
      case 'sold':
        return 'Sold'
      case 'removed_from_market':
        return 'Removed from Market'
      default:
        return container
    }
  }
  
  /**
   * Get movement description
   */
  static getMovementDescription(fromContainer: ContainerType, toContainer: ContainerType): string {
    const from = this.getContainerDisplayName(fromContainer)
    const to = this.getContainerDisplayName(toContainer)
    return `Move from ${from} to ${to}`
  }
}
